/**
 * storefront.en — the English translation of the buyer-facing storefront
 * catalog, composed into the shared `en` object by `messages.en.ts` (never a
 * standalone consumer surface of its own). Content authority runs the other
 * way from the rest of the catalog: `storefront.de.ts` carries the audited
 * Du copy byte-faithful to the view, and this file is its English
 * translation column — while `messages.en.ts`'s composed object still stays
 * the one `MessageKey` type source across every namespace, storefront
 * included.
 *
 * Namespace:
 *   - `brandBar.*`     — the mark-plus-language chrome every storefront route
 *                        mounts (`packages/views/src/storefront/
 *                        brand-bar.view.ts`).
 *   - `orders.*`      — the buyer's own list of what this browser bought, and
 *                        the chrome control that opens it
 *                        (`packages/views/src/storefront/orders/`).
 *   - `eventIdentity.*`— the when/what/where block the event page and
 *                        order-access both mount to name the event
 *                        (`packages/views/src/storefront/
 *                        event-identity.view.ts`).
 *   - `eventDescription.*`
 *                      — the event read whole in an overlay, mounted by
 *                        `eventIdentity`'s two doors
 *                        (`packages/views/src/storefront/
 *                        event-description.view.ts`).
 *   - `landing.*`      — the published-events poster grid
 *                        (`packages/views/src/storefront/landing/landing.view.ts`).
 *   - `eventHeader.*`  — the poster frame around `<event-identity>` + the
 *                        not-found recovery state
 *                        (`packages/views/src/storefront/event-header/event-header.view.ts`).
 *   - `programmeDaySelector.*`
 *                      — the sticky day chips above the ticket list, on an
 *                        event whose types declare their own entry windows
 *                        (`packages/views/src/storefront/
 *                        programme-day-selector.view.ts`).
 *   - `typeSelector.*` — the ticket-type list, its two sold-out states (the
 *                        event's and the selected day's), the whole-event
 *                        band beneath the day's list, per-row stepper
 *                        aria-labels
 *                        (`packages/views/src/storefront/type-selector.view.ts`).
 *   - `eventPage.*`    — contact fields, payment status lines, the test-mode
 *                        banner, the refusal frame, and the post-payment
 *                        loading state held until the mounted `.sync` pushes
 *                        the buyer to `/order`
 *                        (`packages/views/src/storefront/event-page/event-page.view.ts`).
 *                        `payArea.disclosures.*` is what
 *                        `<checkout-disclosures>` renders around the pay CTA —
 *                        the total-price-incl.-VAT fact above the payment
 *                        block, the terms clause beneath it, and under that
 *                        the ad-report cue a shop with a registered Meta pixel
 *                        owes where the e-mail is typed, all flush right;
 *                        the privacy and imprint links on that same footnote
 *                        carry the `legal.footer.*` labels
 *                        (`packages/views/src/storefront/
 *                        checkout-disclosures.view.ts`).
 *                        `eventPage.notice.*` is the one block the page never
 *                        renders itself: it is handed to `<schmancy-revolute>`
 *                        through `.noticeCopy`/`.retryPayLabel`, and the widget
 *                        raises it inside its own stack above the pay button.
 *                        One entry per member of that widget's failure union,
 *                        keyed by what the buyer can DO next — the vendor's own
 *                        wording never reaches a screen, so the classification
 *                        alone decides which words the buyer reads.
 *
 *   - `voucher.*`    — the buyer's discount/access code: the run of text on
 *                        the § 312j price band that opens it, and the overlay
 *                        it opens
 *                        (`packages/views/src/storefront/voucher/`).
 *
 * A `One`/`Other` key pair covers a count-driven word choice the source
 * renders as a ternary (`qty === 1 ? "Ticket" : "Tickets"`).
 */
export const storefront = {
	brandBar: {
		homeAriaLabel: "{{brand}}: go to all events",
		homeAriaLabelUnnamed: "Go to the home page",
	},
	// The buyer's own list of what this browser bought
	// (`packages/views/src/storefront/orders/`), and the chrome control that
	// opens it from `brandBar` and the landing header.
	orders: {
		title: "Your orders",
		// Word for word the surface's own title: the control and the place it
		// opens are named the same thing everywhere.
		entryLabel: "Your orders",
		// The label the hummingbird carries over that entry when the event on
		// screen is one this browser already holds tickets for.
		ticketsHere: "Your tickets are here",
		browserNote: "Everything bought in this browser.",
		loadingAriaLabel: "Loading your orders",
		ticketsOne: "1 ticket",
		ticketsOther: "{{count}} tickets",
		ticketLabelOne: "Ticket",
		ticketLabelOther: "Tickets",
		// The plain sentence a marked row leans on — the treatment is never the
		// only carrier, and this rides the row's accessible name too.
		noteAuthorised:
			"Your payment is still going through. Your tickets appear here as soon as it's done.",
		noteRefunded:
			"This order was refunded \u2014 these tickets no longer work at the door.",
		noteCancelled:
			"This order was cancelled \u2014 these tickets no longer work at the door.",
		emptyTitle: "Nothing here yet",
		emptyBody:
			"Orders stay with the browser they were bought in, and this one hasn't bought any yet. Bought your tickets on your phone, or in another browser? The link in your confirmation email opens them anywhere.",
		emptyCta: "Browse events",
		errorTitle: "We couldn't load your orders",
		errorBody:
			"Try again in a moment. The link in your confirmation email still opens your tickets in the meantime.",
		cappedNote: "Showing your {{count}} most recent orders.",
	},
	eventIdentity: {
		venueMapAriaLabel: "{{venue}}: open map",
		entryInfoAriaLabel: "Entry information",
		// Identical wording to `typeSelector.descriptionButtonAriaLabel`: the
		// clamped description and a described ticket type offer the same thing with
		// the same gesture, so one sentence names both.
		descriptionButtonAriaLabel: "More information about {{name}}",
		// The full name leads: `text-overflow` shortens what the eye reads and
		// never touches the accessible text, so this is the one place the whole
		// name is guaranteed to arrive. The verb, not the word order, is what
		// separates this from the description's door two rows below: both open the
		// same overlay, so each says the thing only it offers — that one more
		// information, this one the name that was cut.
		nameButtonAriaLabel: "{{name}} — show in full",
	},
	eventDescription: {
		closeAriaLabel: "Close",
		// Same fact and wording as `eventIdentity.venueMapAriaLabel`, kept as
		// its own key: this overlay's copy lifecycle is this component's own.
		venueMapAriaLabel: "{{venue}}: open map",
		entryInfoAriaLabel: "Entry information",
	},
	landing: {
		loadingAriaLabel: "Loading events",
		emptyTitle: "No events yet",
		emptyBody:
			"The first events from {{name}} will appear here — check back soon.",
		// The disclosure's own label AND the accessible name of the group it
		// opens. The count rides the sentence rather than a badge beside it, so
		// the name read aloud is a whole phrase and never a floating number.
		pastOne: "1 past event",
		pastOther: "{{count}} past events",
		soldOutBadge: "Sold out",
		// Only on a card whose return window is open right now; the same words
		// the return page itself leads with.
		refundButton: "Return tickets",
	},
	eventHeader: {
		notFoundTitle: "This event no longer exists",
		notFoundBody:
			"It may be over, or the link is outdated. You'll find every current event on the homepage.",
		backToEvents: "See all events",
		posterAriaLabel: "{{name}}: enlarge poster",
	},
	programmeDaySelector: {
		groupAriaLabel: "Choose a day",
		// The whole pill read as one sentence: the calendar pill stacks
		// weekday, day and month as three separate lines, and read out one by
		// one they are three fragments rather than a date. The entry hours
		// belong in it because the chip is a choice about when the door is
		// open, not just about a date.
		dayAriaLabel: "{{day}}, {{opensAt}} – {{closesAt}}",
		// Same sentence, plus what the chip's dot says silently to everyone
		// else — this day is already in the order.
		dayInOrderAriaLabel:
			"{{day}}, {{opensAt}} – {{closesAt}} — already in your basket",
	},
	typeSelector: {
		soldOutTitle: "Sold out",
		soldOutBody:
			"No tickets are available for this event right now. You'll find every current event on the homepage.",
		// Nothing in this catalog is offered to the public — every type is for
		// the organiser's team or behind a code. Nothing sold out, so it never
		// says so.
		notOnSaleTitle: "Not sold online",
		notOnSaleBody:
			"Tickets for this event aren't sold online. You'll find every current event on the homepage.",
		// The day, not the event: every other chip still leads somewhere, so
		// this says which one is gone and where to go next — never "for this
		// event", which reads as a dead event while five days are still on
		// sale.
		daySoldOutTitle: "Sold out for this day",
		daySoldOutBody:
			"No tickets are available for this day right now. Pick another day above.",
		// The band beneath the day's list — the tickets that are not tied to
		// one day. It names what they ARE, so nobody reads them as one more of
		// today's options and wonders whether they need one per day.
		wholeEventTitle: "For the whole event",
		orderCap: "Maximum {{cap}} tickets per order.",
		orderCapOne: "One ticket per order.",
		free: "Free",
		volumeDiscount: "from {{minQty}} tickets {{price}}",
		lowStock: "Only a few left",
		soldOutAfterSelection:
			"Sold out in the meantime — your selection isn't guaranteed",
		decrementAriaLabel: "{{name}}: decrease quantity",
		incrementAriaLabel: "{{name}}: increase quantity",
		soldOutStatus: "Sold out",
		opensOn: "from {{date}}",
		saleEnded: "Sales ended",
		basketLocked: "Locked while we confirm your payment. — {{editBasket}}",
		editBasket: "Edit basket",
		// Two doors onto one overlay, announced one after the other, so each
		// says the thing only it offers — the icon more information, the name
		// itself, which `text-overflow` may have cut on screen and never in the
		// accessible text. Same split, same reasoning, as `eventIdentity`'s
		// pair two blocks above.
		descriptionButtonAriaLabel: "More information about {{name}}",
		nameButtonAriaLabel: "{{name}} — show in full",
		// The description overlay's own footer button. "Back" and not "Close":
		// it returns the buyer to the list they were reading, and the stepper
		// beside it means the overlay is a place a purchase can be made rather
		// than a message to be dismissed.
		descriptionBackLabel: "Back",
	},
	eventPage: {
		cardDisclosureLabel: "Pay with card",
		nameLabel: "Name",
		billingCountryLabel: "Billing country",
		billingPostcodeLabel: "Billing postcode",
		billingCountryRequired: "Choose your billing country",
		billingPostcodeRequired: "Enter your billing postcode",
		nameRequired: "Please enter your name.",
		emailLabel: "Email",
		emailRequired: "Please enter your email address.",
		emailInvalid: "Please enter a valid email address.",
		addressLabel: "Street, postcode and city",
		addressReason:
			"Above {{ceiling}} your address has to appear on the invoice.",
		addressRequired: "Please enter your address.",
		refundButton: "Return tickets",
		jumpBar: {
			ticketOne: "{{qty}} ticket · {{total}}",
			ticketOther: "{{qty}} tickets · {{total}}",
			payCta: "Go to payment",
		},
		payArea: {
			payLabel: "Pay · {{total}}",
			// payLabel's verb without the total — the word the terms clause
			// quotes, so the quote reads as the button the buyer is looking at.
			payVerb: "Pay",
			payLabelSettling: "Updating…",
			contactNeededToast: "Add your name and email to continue.",
			addressNeededToast: "Add your invoice address to continue.",
			freeOrder: "Order for free",
			unavailable: "Payment isn't available right now.",
			salesPaused: "Ticket sales are paused right now.",
			noPaymentSession: "Payment isn't ready yet",
			testModeBanner: "Test mode · no real charge",
			disclosures: {
				terms: "“{{action}}” means you accept our {{terms}}.",
				// Art. 13 at the point of collection: the buyer types an email
				// address into the field above this line, and where the organiser
				// has a Meta pixel registered that address leaves for an ad
				// platform after the purchase on legitimate interest, not on
				// anything the buyer was asked. One sentence naming what is sent,
				// who receives it, and that the buyer may object — the policy the
				// link on the next row opens carries the recipient's full identity,
				// the onward US transfer and the objection's mechanics, so this
				// stays a cue and never restates them. No link of its own: the
				// privacy link sits directly beneath it, and a second copy of the
				// same link inside the sentence would only split the reader's
				// attention between two routes to one document.
				adsReport:
					"After your purchase we report the order value and your email address in hashed form to Meta — you can object to this at any time.",
				totalSingleRate: "{{total}} incl. {{rate}}% VAT",
				totalMixedRate: "{{total}} incl. VAT",
			},
		},
		statusLine: {
			confirming: "Confirming payment…",
			prepareUnavailable: "Payments are temporarily unavailable.",
			retry: "Try again",
			preparing: "Preparing payment…",
			syncing: "Updating amount…",
		},
		notice: {
			insufficientFunds: {
				headline: "There isn't enough available on the card",
				body: "Nothing was charged. Use a different card, or try again later — your selection is still saved.",
			},
			expiredCard: {
				headline: "The card has expired",
				body: "Nothing was charged. Use a card that's still valid — your selection is still saved.",
			},
			cardDetails: {
				headline: "The card details don't match",
				body: "Nothing was charged. Check the card number, expiry date and security code — your selection is still saved.",
			},
			// The only member the buyer fixes on this page rather than on a card:
			// name and email are fields this form itself collects, so the copy
			// points back up at them.
			contactDetails: {
				headline: "The contact details don't match",
				body: "Nothing was charged. Check the name and email address above — your selection is still saved.",
			},
			authentication: {
				headline: "Your bank's confirmation is missing",
				body: "Nothing was charged. Try again and complete the confirmation — your selection is still saved.",
			},
			bankDeclined: {
				headline: "Your bank declined the payment",
				body: "Nothing was charged. Check with your bank, or use a different card — your selection is still saved.",
			},
			// The PIN is what is locked, and it is never entered on this page —
			// naming it would send the buyer looking for a field that does not
			// exist, so the copy names only the move that works here.
			cardLocked: {
				headline: "The card is locked",
				body: "Nothing was charged. Use a different card, since only your bank can lift the lock — your selection is still saved.",
			},
			// A limit lifts on its own, which is why "later" leads here and the
			// second card follows it — the reverse of insufficientFunds, where the
			// money may never arrive on that card at all.
			limitReached: {
				headline: "The card's limit is reached",
				body: "Nothing was charged. Try again later, or use a different card — your selection is still saved.",
			},
			cardNotActivated: {
				headline: "The card isn't activated yet",
				body: "Nothing was charged. Activate it with your bank, or use a different card — your selection is still saved.",
			},
			cardNotSupported: {
				headline: "This card isn't accepted here",
				body: "Nothing was charged. Use a different card — your selection is still saved.",
			},
			technical: {
				headline: "That was on us, not your card",
				body: "Nothing was charged. Try again later — your selection is still saved.",
			},
			refused: {
				headline: "Your payment didn't go through",
				body: "Nothing was charged. Try again or use a different card — your selection is still saved.",
			},
			// The one member that cannot end on "your selection is still saved":
			// the checkout the selection belonged to is exactly what is gone, so
			// the copy sends the buyer back to the start instead of promising a
			// basket that will not be there.
			orderStale: {
				headline: "This order is no longer valid",
				body: "Nothing was charged. Reload the page and choose your tickets again.",
			},
			blocked: {
				headline: "Payments are temporarily unavailable.",
				body: "",
				remedy: "Try again",
			},
			// The retry tense of `payArea.payLabel`, carrying the same total: the
			// widget relabels the CTA verbatim and cannot inject an amount, so the
			// host composes it here exactly as it composes the pay label.
			retryPay: "Try again · {{total}}",
		},
		refusal: {
			title: "Unfortunately, that didn't work",
			body: "The tickets you picked were taken by someone else before your payment could go through. No money was charged — if your bank shows the amount as reserved, that clears on its own, usually within a few days.",
			bodyBasketChanged:
				"Your basket changed before payment went through — nothing was charged.",
			bodyGeneric: "Something interrupted your payment — nothing was charged.",
			restart: "Back to ticket selection",
			continueWithTotal: "Continue with your updated total",
		},
		processing: {
			title: "Processing your order…",
			body: "One moment — please keep this window open.",
		},
	},
	// See storefront.de.ts for why every refusal cause shares one sentence.
	voucher: {
		row: "Redeem a code",
		triggerAppliedAriaLabel: "Code {{code}} — change code",
		label: "Discount or access code",
		placeholder: "e.g. PRESSE25",
		apply: "Apply",
		remove: "Remove",
		done: "Done",
		cancel: "Close",
		checking: "Checking your code …",
		applied: "Code applied.",
		appliedUnlock: "Code applied. {{types}} is now available to you.",
		appliedUnlockOnly:
			"Code applied. {{types}} is now available to you. Your current total stays the same.",
		youSaved: "You saved {{amount}}",
		showTickets: "Go to the tickets",
		rules:
			"Codes are usually tied to one event, to certain tickets, to a period of time, or to one use per person. Capitals don't matter.",
		refusedHeadline: "This code can't be applied",
		refusedBody:
			"Codes are usually tied to one event, to certain tickets, to a period of time, or to one use per person. Check the spelling — capitals don't matter.",
		refusedAgain:
			"Check the spelling once more. If it still doesn't work, ask whoever gave you the code — we can't see why from here.",
		spentHeadline: "Too many attempts",
		spentBody:
			"We won't take another code on this order. Finish without one, or start a new order once you have the right code.",
		dropped: "{{code}} no longer fits your selection and has been removed.",
	},
	orderAccess: {
		notFound: {
			title: "This link doesn't open any tickets",
			body: "Either the address is incomplete — check the link in the message you got it in. Or the tickets have since been taken back — then this link no longer works. If someone sent you the link, ask them.",
			cta: "Browse events",
		},
		success: {
			headline: "You're in.",
			subline: "Your ticket for {{eventName}} is ready.",
		},
		qrHint: "Shown at entry",
		qrAriaLabel: "Admission QR code for {{typeName}}",
		ticketAriaLabel: "Ticket {{displayId}}, {{typeName}}",
		thankYou: "Thanks for being part of it.",
		handover: {
			whoFor: "Who's it for?",
			heldBy: "For {{name}} · {{channel}}",
			whoForAriaLabel: "Who is {{typeName}} {{displayId}} for?",
			heldByAriaLabel: "{{typeName}} {{displayId}}: {{holder}}",
			channel: {
				whatsapp: "via WhatsApp",
				email: "via email",
				share: "shared",
				inPerson: "in person",
			},
			tally: {
				all: "All {{count}}",
				yours: "{{count}} still with you",
				given: "{{count}} given away",
			},
			sheet: {
				retry: "Failed · Try again",
				title: "Who's it for?",
				nameLabel: "Name",
				namePlaceholder: "A first name is enough",
				nameHint:
					"A first name or nickname is enough to remember who has which ticket.",
				ticketsOne: "{{type}} · {{displayId}}",
				ticketsPerType: "{{count}}× {{type}}",
				ticketsNone: "No ticket selected yet",
				pickerAdd: "Add more tickets",
				pickerCount: "{{count}} tickets selected",
				wholeEvent: "For the whole event",
				heldByYou: "{{count}} still with you",
				heldByOther: "{{count}} with {{name}}",
				typeOnDay: "{{type}}, {{date}}",
				typeWholeEvent: "{{type}}, whole event",
				rowAriaLabel: "{{type}}, {{count}} still with you",
				addAriaLabel: "Add {{type}}",
				removeAriaLabel: "Remove {{type}}",
				qtyAriaLabel: "Number of {{type}}",
				channelLabel: "And how?",
				whatsapp: "Send via WhatsApp",
				email: "Send via email",
				share: "Share …",
				inPerson: "Just note it, send nothing",
				preparing: "Preparing the link …",
				mailRefused: {
					headline: "We couldn't send the email",
					body: "It's still noted for {{name}}, and the link is valid. Send it to them another way.",
				},
				reclaim: "Take the tickets back",
			},
			recordedToast: "Noted for {{name}}.",
			sentToast: "Noted for {{name}}. The link is on its way by email.",
			openedToast:
				"Noted for {{name}}. Your message is open — you still need to send it.",
			reclaimedToast: "The tickets are back with you.",
			error: "That didn't go through. Try again.",
			sendError:
				"The note stands, but the message wouldn't open. Tap the name on the ticket to send the link again.",
			emailAddress: {
				retryMail: "Saved · Retry email",
				title: "Send via email",
				lead: "Where should the link for {{name}} go?",
				label: "Email address",
				placeholder: "name@example.com",
				hint: "Used for this one mail only, and not stored.",
				send: "Send the link",
				invalid: "That email address isn't quite right. Give it another look.",
			},
			message: {
				subject: "Your ticket for {{eventName}}",
				subjectPlural: "Your tickets for {{eventName}}",
				greeting: "Hi {{name}},",
				lead: "here is your ticket for {{eventName}}:",
				leadPlural: "here are your {{count}} tickets for {{eventName}}:",
				ticketLine: "{{typeName}} · {{displayId}}",
			},
		},
		artifacts: {
			pdf: "PDF ticket",
			ics: "Add to calendar",
			receipt: "Invoice",
			walletApple: "Apple Wallet",
			walletGoogle: "Google Wallet",
			preparing: "{{artifact}}: Preparing",
		},
		downloadTickets: {
			label: "Download tickets",
			ariaLabel: "Download all tickets",
			preparingAriaLabel: "Saving ticket {{done}} of {{total}}",
			progress: "{{done}}/{{total}}",
			error: "Couldn't prepare your tickets. Please try again.",
		},
		shareOrder: {
			label: "Share order",
			ariaLabel: "Share this order",
			text: "Tickets for {{eventName}}",
			savedToast: "Link copied",
			error: "Couldn't share this link. Please try again.",
		},
		walletGoogleBadge: {
			ariaLabel: "Add to Google Wallet",
			ariaLabelIndexed: "Add to Google Wallet, {{index}} of {{count}}",
			preparingAriaLabel: "Add to Google Wallet: Preparing",
		},
		walletAppleBadge: {
			ariaLabel: "Add to Apple Wallet",
			ariaLabelIndexed: "Add to Apple Wallet, {{index}} of {{count}}",
			preparingAriaLabel: "Add to Apple Wallet: Preparing",
		},
		ticketActions: {
			ariaLabel: "{{typeName}}: keep this ticket",
			savingAriaLabel: "Saving {{typeName}}",
			unavailableAriaLabel: "{{typeName}}: can no longer be kept",
			saveImage: "Save as image",
			retryImage: "Retry saving image",
		},
		ticketStatus: {
			valid: "Valid",
			redeemed: "Already scanned",
			cancelled: "Cancelled",
		},
		voided: {
			refundedTitle: "This order was refunded",
			refundedBody:
				"The full amount has been refunded to your original payment method. These tickets are no longer valid for entry.",
			cancelledTitle: "This order was cancelled",
			cancelledBody:
				"These tickets are no longer valid for entry. If you're expecting a refund, it's on its way to your original payment method.",
		},
	},
	refund: {
		title: "Return your tickets",
		openUntil: "Return by {{date}}",
		// The operator's own note renders here; this is the fallback when the
		// event carries none. One line — the rows are the interface.
		defaultMessage:
			"Upload your tickets. Part of an order is fine — the rest stays valid.",
		pick: "Choose tickets",
		dropAddMore: "Add more",
		// The one line that keeps the surface honest.
		localOnly: "Files stay on your device — we only read the code.",
		pageLabel: "Page {{page}}",
		removeAriaLabel: "Remove {{fileName}}",
		thumbAlt: "Ticket from {{fileName}}",
		// Per-file states, four words at most: the row's ground already says
		// good / soft no / hard no, so the line only has to name which.
		fileDecoding: "Reading …",
		fileChecking: "Checking …",
		fileCheckingSlow: "Still checking …",
		fileNoCode: "No code in this image",
		fileNotOurs: "Not one of our tickets",
		fileUnfinished: "Couldn't finish",
		fileReaderFailed: "Reload this page",
		reloadAriaLabel: "Reload this page",
		fileDuplicate: "This ticket is a duplicate",
		fileWrongType: "Images and PDFs only",
		fileOverCap: "Not taken",
		fileStalled: "Couldn't be checked",
		capReached: "More than {{cap}} won't go in one go.",
		confirmFailed: "That didn't go through. Try again.",
		verdict: {
			notEligible: "Not refundable",
			alreadyRefunded: "Already refunded",
			alreadyScanned: "Already scanned",
			notFound: "No such ticket",
			noneBody: "None of this comes back.",
			totalLabel: "Refund total",
			totalCount: "{{count}} ticket",
			totalCountPlural: "{{count}} tickets",
		},
		// One plain line under the total, and no struck price anywhere: a figure
		// crossed out on a buyer's own screen reads as being given less, when
		// what it would be saying is that the discount they used was theirs and
		// is already accounted for. Said once, for the whole list.
		discountIncluded:
			"Each amount is what you paid for that ticket — any discount you used is already taken off.",
		consequence:
			"The tickets are cancelled and the money goes back to the card that paid.",
		action: "Refund {{total}}",
		retryAction: "Retry {{total}} refund",
		running: "Refund running …",
		// Not "Refunded": the confirm lands a batch the refund consumer spends
		// afterwards, so a settled claim here is one this page cannot back yet.
		settledHeadline: "On its way",
		settled: "{{total}} is on its way back to the card that paid.",
		again: "Return more tickets",
		closedBody: "The return period closed on {{date}}.",
		notYetBody: "Returns open on {{date}}.",
		unconfiguredBody:
			"This event doesn't take returns. Pass your ticket on — it works at the door just the same.",
		contact: "Something not right? Write to us at {{email}}.",
		pickTitle: "Which event is this about?",
		pickNone: "No event is taking tickets back right now.",
	},
} as const;
